/**
 * ScheduleSlots Filters Component
 * Inline filter bar: filters stay visible and apply live (no popover).
 */
import React from 'react';
import { TextField } from '@mui/material';
import { InlineFilterBar, InlineSelect } from '../../../components/common';
import { useFilterState } from '../../../hooks/useFilterState';
import {
  SlotStatus,
  SlotAppointmentType,
  SLOT_STATUS_LABELS,
  SLOT_APPOINTMENT_TYPE_LABELS,
} from '../../../types/scheduleSlot';

export interface SlotFiltersData {
  searchTerm: string;
  companyId: string;
  status: string;
  appointmentType: string;
  startDate: string;
  endDate: string;
}

interface Props {
  onFilter: (filters: SlotFiltersData) => void;
  companies: Array<{ id: number; name: string }>;
  loading?: boolean;
  total?: number;
}

const STATUS_OPTIONS = Object.values(SlotStatus).map((s) => ({
  value: s,
  label: SLOT_STATUS_LABELS[s],
}));
const APPOINTMENT_TYPE_OPTIONS = Object.values(SlotAppointmentType).map((t) => ({
  value: t,
  label: SLOT_APPOINTMENT_TYPE_LABELS[t],
}));

const defaultValues: SlotFiltersData = {
  searchTerm: '',
  companyId: '',
  status: '',
  appointmentType: '',
  startDate: '',
  endDate: '',
};

export const ScheduleSlotsFilters: React.FC<Props> = ({
  onFilter,
  companies,
  loading = false,
  total,
}) => {
  const getCompanyName = (id: string) => companies.find((c) => c.id === Number(id))?.name ?? id;

  const COMPANY_OPTIONS = companies.map((c) => ({ value: String(c.id), label: c.name }));

  const { filters, setFilter, clearAll, activeChips } = useFilterState(defaultValues, {
    onFilter,
    applyOnMount: true,
    live: true,
    labels: {
      companyId: 'Empresa',
      status: 'Status',
      appointmentType: 'Tipo',
      startDate: 'Data Início',
      endDate: 'Data Fim',
    },
    formatValue: {
      companyId: (v: string) => getCompanyName(v),
      status: (v: string) => SLOT_STATUS_LABELS[v as SlotStatus] ?? v,
      appointmentType: (v: string) => SLOT_APPOINTMENT_TYPE_LABELS[v as SlotAppointmentType] ?? v,
    },
  });

  return (
    <InlineFilterBar
      searchPlaceholder="Buscar por nome do médico, local..."
      searchValue={filters.searchTerm}
      onSearchChange={(v) => setFilter('searchTerm', v)}
      resultCount={total}
      resultNoun="horários"
      chips={activeChips}
      onClearAll={clearAll}
      loading={loading}
    >
      <InlineSelect
        label="Empresa"
        value={filters.companyId}
        options={COMPANY_OPTIONS}
        onChange={(v) => setFilter('companyId', v)}
        emptyLabel="Todas"
        minWidth={160}
      />
      <InlineSelect
        label="Status"
        value={filters.status}
        options={STATUS_OPTIONS}
        onChange={(v) => setFilter('status', v)}
        emptyLabel="Todos"
        minWidth={160}
      />
      <InlineSelect
        label="Tipo de Atendimento"
        value={filters.appointmentType}
        options={APPOINTMENT_TYPE_OPTIONS}
        onChange={(v) => setFilter('appointmentType', v)}
        emptyLabel="Todos"
        minWidth={190}
      />
      <TextField
        size="small"
        type="date"
        label="Data Início"
        value={filters.startDate}
        onChange={(e) => setFilter('startDate', e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
      />
      <TextField
        size="small"
        type="date"
        label="Data Fim"
        value={filters.endDate}
        onChange={(e) => setFilter('endDate', e.target.value)}
        InputLabelProps={{ shrink: true }}
        sx={{ minWidth: 160 }}
      />
    </InlineFilterBar>
  );
};

export default ScheduleSlotsFilters;
